import { defineComposable } from '@/core/utils/defineComposable'
import { useBillingStore } from './useBillingStore'
import { useCreditUsageStore } from './useCreditUsageStore'
import { useCreditUsageLoader } from './useCreditUsageLoader'
import { useTeamStore } from '@/pinia/useTeamStore'
import { getRainbowColor } from '@/uiKit/colorPalette'
import { computed } from 'vue'
import type { ClientTeamLimitMap, UsageInfo } from './types'

export const useCreditMetrics = defineComposable(() => {
  const billingStore = useBillingStore()
  const creditUsageStore = useCreditUsageStore()
  const creditUsageLoader = useCreditUsageLoader()
  const teamStore = useTeamStore()

  const creditAvailable = computed(() => creditUsageStore.creditUsage?.total_available_hours || 0)

  const storageAvailable = computed(() => billingStore.fileStorageLimit || 0)

  /**
   * Retrieves the credit limit for the parent team
   */
  const parentCreditLimit = computed(() => {
    const subscription = billingStore.billingInfo?.customer_subscription
    const limit = subscription?.annotation_credits_standard_max_in_period ?? 0
    const bonus = subscription?.annotation_credits_bonus ?? 0
    return limit + bonus
  })

  /**
   * Retrieves the storage limit for the parent team
   */
  const parentStorageLimit = computed(() => {
    const subscription = billingStore.billingInfo?.customer_subscription
    const limit = subscription?.storage_standard_max_in_period ?? 0
    const extra = subscription?.storage_extra ?? 0
    return limit + extra
  })

  /**
   * Calculates the remaining credits available
   */
  const remainingCredits = computed(() => {
    const subscription = billingStore.billingInfo?.customer_subscription
    if (!subscription) return 0
    const totalLimit = parentCreditLimit.value
    const used = subscription.annotation_credits_used
    return Math.max(totalLimit - used, 0)
  })

  /**
   * Computes credit usage information for all client teams
   */
  const clientTeamsInfo = computed<UsageInfo[]>(() => {
    if (!creditUsageStore.creditUsage || !teamStore.currentTeam || !billingStore.billingInfo) {
      return []
    }

    return creditUsageStore.creditUsage?.clients?.flatMap((clientInfo, index) => {
      const client = teamStore.currentTeam?.clients.find((t) => t.id === clientInfo.team_id)
      if (!client) {
        return []
      }

      const billingInfo = billingStore.billingInfo?.clients.find(
        ({ customer: { team_id: teamId } }) => teamId === clientInfo.team_id,
      )
      const subscription = billingInfo?.customer_subscription

      const creditLimit =
        (subscription?.annotation_credits_standard_max_in_period ?? 0) +
        (subscription?.annotation_credits_bonus ?? 0)
      const shouldShowCreditLimit = creditLimit !== parentCreditLimit.value

      const storageLimit =
        (subscription?.storage_standard_max_in_period ?? 0) + (subscription?.storage_extra ?? 0)
      const shouldShowStorageLimit = storageLimit !== parentStorageLimit.value

      return [
        {
          id: client.slug,
          name: client.name,
          color: getRainbowColor(index + 1),
          isChild: true,
          credit: {
            human: clientInfo.used_human_hours,
            automation: clientInfo.used_automation_hours,
            model: clientInfo.used_model_hours,
            client: clientInfo.total_client_used_hours,
            available: clientInfo.total_available_hours,
            used: clientInfo.total_used_hours,
            limit: shouldShowCreditLimit ? creditLimit : undefined,
          },
          storage: {
            used: subscription?.storage_used || 0,
            available: storageAvailable.value || 0,
            managedFiles: subscription?.storage_used || 0,
            limit: shouldShowStorageLimit ? storageLimit : undefined,
          },
        },
      ]
    })
  })

  /**
   * Computes credit usage information for the parent team
   */
  const currentTeamInfo = computed<UsageInfo | null>(() => {
    if (!creditUsageStore.creditUsage) {
      return null
    }

    const subscription = billingStore.billingInfo?.customer_subscription
    const storageAvailable =
      (subscription?.storage_standard_max_in_period || 0) + (subscription?.storage_extra || 0)
    const filesInClient = clientTeamsInfo.value?.reduce(
      (acc, curr) => acc + (curr.storage.filesInClient || 0),
      0,
    )

    return {
      id: teamStore.currentTeam?.slug ?? 'parent-team',
      name: teamStore.currentTeam?.name ?? 'Parent team',
      color: getRainbowColor(0),
      credit: {
        human: creditUsageStore.creditUsage.used_human_hours,
        automation: creditUsageStore.creditUsage.used_automation_hours,
        model: creditUsageStore.creditUsage.used_model_hours,
        client: creditUsageStore.creditUsage.total_client_used_hours,
        available: creditUsageStore.creditUsage.total_available_hours,
        used: creditUsageStore.creditUsage.total_used_hours,
      },
      storage: {
        used: subscription?.storage_used || 0,
        available: storageAvailable,
        managedFiles: subscription?.storage_used || 0,
        filesInClient,
      },
    }
  })

  /**
   * Combines parent and client usage information into a single array
   * Parent team is listed first, followed by all clients
   */
  const allUsageInfo = computed<UsageInfo[]>(() =>
    currentTeamInfo.value
      ? [currentTeamInfo.value, ...clientTeamsInfo.value]
      : clientTeamsInfo.value,
  )

  const creditUsed = computed(() =>
    allUsageInfo.value.reduce((acc, curr) => acc + curr.credit.used, 0),
  )

  const storageUsed = computed(() =>
    allUsageInfo.value.reduce((acc, curr) => acc + curr.storage.used, 0),
  )

  const childTeamsLimitMap = computed(() => {
    if (!clientTeamsInfo.value) {
      return {}
    }

    return clientTeamsInfo.value.reduce<ClientTeamLimitMap>(
      (acc, { id, color, name, credit, storage }) => {
        acc[id] = {
          id,
          color,
          partnerId: teamStore.currentTeam?.id ?? 0,
          name,
          creditLimit: credit.limit,
          storageLimit: storage.limit,
        }
        return acc
      },
      {},
    )
  })

  const hasChildTeams = computed(() => clientTeamsInfo.value.length > 0)

  const init = (): void => {
    creditUsageLoader.loadUsage()
  }

  return {
    creditAvailable,
    creditUsed,
    storageAvailable,
    storageUsed,
    allUsageInfo,
    hasChildTeams,
    childTeamsLimitMap,
    parentCreditLimit,
    remainingCredits,
    init,
  }
})
